import { useLocation } from "react-router-dom";
import { Lightbulb } from "lucide-react";
import { ProcessSection } from "@/components/home/ProcessSection";
import { RoiCalculator } from "@/components/home/RoiCalculator";
import { FaqSection } from "@/components/sections/FaqSection";
import { FinalCta } from "@/components/sections/FinalCta";
import { TextLink } from "@/components/ui/Button";
import { CtaReassurance, FreeAuditButton } from "@/components/ui/Cta";
import { PageHero } from "@/components/ui/PageHero";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { QUIZ_PATH } from "@/config/constants";
import { nicheByPath } from "@/data/niches";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useLocale } from "@/i18n/useLocale";
import { NotFoundPage } from "@/pages/NotFoundPage";

/** One landing page per type of business; the copy lives in src/data/niches.ts. */
export function NichePage() {
  const { pathname } = useLocation();
  const niche = nicheByPath(pathname);
  if (!niche) return <NotFoundPage />;
  return <NicheContent path={niche.path} />;
}

function NicheContent({ path }: { path: string }) {
  usePageSeo(path);
  const locale = useLocale();
  const { t } = useAppTranslation("common");
  const page = nicheByPath(path)![locale];
  const location = `niche_${path.slice(1)}`;

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={page.name}
        title={page.hero.title}
        lead={<p>{page.hero.lead}</p>}
        actions={
          <>
            <div>
              <FreeAuditButton location={`${location}_hero`} />
              <CtaReassurance className="mt-3" />
            </div>
            <TextLink to="/precios">{t("seePlans")}</TextLink>
          </>
        }
      />

      <section aria-labelledby="niche-pains-heading" className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-24 sm:px-6 md:py-28">
          <SectionHeader id="niche-pains-heading" title={page.pains.title} />
          <ul className="mt-12">
            {page.pains.items.map((item, i) => (
              <li
                key={item}
                data-reveal=""
                style={{ ["--reveal-delay" as string]: `${i * 90}ms` }}
                className="flex gap-5 border-t border-brand-primary/10 py-6 first:border-t-0 first:pt-0"
              >
                <span aria-hidden="true" className="mt-[0.9rem] h-0.5 w-7 shrink-0 rounded-full bg-brand-secondary" />
                <span className="text-lg leading-relaxed text-ink-soft">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="niche-plan-heading" className="bg-brand-bg">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
          <SectionHeader id="niche-plan-heading" title={page.plan.title} lead={page.plan.lead} />
          <ol className="mt-14 grid gap-6 sm:grid-cols-2">
            {page.plan.items.map((item, i) => (
              <li
                key={item.title}
                data-reveal=""
                style={{ ["--reveal-delay" as string]: `${i * 80}ms` }}
                className="rounded-[1.75rem] border border-brand-primary/10 bg-white p-7 shadow-lit sm:p-9"
              >
                <span className="font-display text-lg font-semibold text-teal-strong [font-variant-numeric:lining-nums_tabular-nums]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 font-display text-2xl font-semibold text-brand-primary">{item.title}</h3>
                <p className="mt-3 leading-relaxed text-ink-soft">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section aria-labelledby="niche-ideas-heading" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
          <SectionHeader id="niche-ideas-heading" title={page.ideas.title} lead={page.ideas.lead} />
          <ul className="mt-12 flex flex-wrap gap-3">
            {page.ideas.items.map((idea) => (
              <li
                key={idea}
                className="inline-flex items-center gap-2.5 rounded-full border border-brand-primary/12 bg-brand-bg px-5 py-3 text-ink"
              >
                <Lightbulb aria-hidden="true" className="size-4 shrink-0 text-teal-strong" />
                {idea}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <TextLink to={QUIZ_PATH}>{t("quizCta")}</TextLink>
          </div>
          <RoiCalculator />
        </div>
      </section>

      <ProcessSection />
      <FaqSection path={path} />
      <FinalCta location={`${location}_final`} />
    </main>
  );
}
